// Petty cash claims (backend services/pettyCash*.ts; docs/PETTY_CASH.md). Amounts are numbers in the
// company's currency; dates are 'YYYY-MM-DD'. Dimensions are keyed by SAP dimension number '1'..'5'.

export type ClaimStatus =
  | 'Draft' | 'Pending' | 'FinanceReview' | 'Posting' | 'Posted' | 'PartiallyPosted' | 'Returned' | 'Rejected' | 'Cancelled';
export type LineType = 'VAT' | 'NonVAT';
export type LineStatus = 'Open' | 'Disallowed' | 'InvoicePosted' | 'Paid' | 'Failed';
export type AllocationType = 'Project' | 'CostCenter';
export type Dims = Record<string, string>;

export const CLAIM_STATUS: Record<ClaimStatus, { label: string; color: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' }> = {
  Draft: { label: 'Draft', color: 'default' },
  Pending: { label: 'In approval', color: 'info' },
  FinanceReview: { label: 'Finance review', color: 'warning' },
  Posting: { label: 'Posting to SAP', color: 'info' },
  Posted: { label: 'Posted', color: 'success' },
  PartiallyPosted: { label: 'Partly posted', color: 'error' },
  Returned: { label: 'Returned', color: 'warning' },
  Rejected: { label: 'Rejected', color: 'error' },
  Cancelled: { label: 'Cancelled', color: 'default' },
};

/** The status a claim shows: a posted claim whose holder was paid back reads Reimbursed. */
export type DisplayStatus = ClaimStatus | 'Reimbursed';

export const DISPLAY_STATUS: Record<DisplayStatus, (typeof CLAIM_STATUS)[ClaimStatus]> = {
  ...CLAIM_STATUS,
  Reimbursed: { label: 'Reimbursed', color: 'success' },
};

export const LINE_STATUS: Record<LineStatus, { label: string; color: 'default' | 'success' | 'warning' | 'error' | 'info' }> = {
  Open: { label: 'Open', color: 'default' },
  Disallowed: { label: 'Disallowed', color: 'warning' },
  InvoicePosted: { label: 'Invoice posted', color: 'info' },
  Paid: { label: 'Posted', color: 'success' },
  Failed: { label: 'SAP refused', color: 'error' },
};

export type ImprestCheckMode = 'off' | 'warn' | 'block';
export type ReimburseMethod = 'BankTransfer' | 'Cash' | 'Cheque' | 'Other';

/** How the holder was paid back; a bank transfer and a cheque need their reference. */
export const REIMBURSE_METHODS: Array<{ value: ReimburseMethod; label: string; needsReference: boolean }> = [
  { value: 'BankTransfer', label: 'Bank transfer', needsReference: true },
  { value: 'Cash', label: 'Cash', needsReference: false },
  { value: 'Cheque', label: 'Cheque', needsReference: true },
  { value: 'Other', label: 'Other', needsReference: false },
];

/** The account a reimbursement's SAP top-up credits for one way of paying, with its auxiliary (SAP Dimension 5). */
export interface ReimburseAccountSetting {
  account: string;
  accountName: string | null;
  auxiliary: string | null;
  auxiliaryName: string | null;
}

/** The ways of paying that may credit their own account; a bank transfer credits the bank account. */
export const MAPPED_REIMBURSE_METHODS: ReimburseMethod[] = ['Cash', 'Cheque', 'Other'];

/** A finance stage after the finance review: who approves it (any holder of the role). */
export interface FinanceStageSetting { name: string; role: string }

/**
 * The imprest, reimbursement, quarterly confirmation, originals and finance stage settings (Oct 2026). Optional
 * on PettyCashSettings so a server without them still works: `processSettings()` gives the defaults.
 */
export interface PettyCashProcessSettings {
  /** The imprest check on a claim: float - claims not yet reimbursed - this claim - advances = expected cash. */
  imprestCheck: ImprestCheckMode;
  /** The difference allowed between the cash counted and the expected cash. */
  imprestTolerance: number;
  /** Working days from the finance review to the reimbursement (1 to 60). */
  reimburseTargetDays: number;
  /** Days not counted as working days, 0 = Sunday ... 6 = Saturday. */
  weekendDays: number[];
  /** Post the top-up journal in SAP when a reimbursement is recorded (debit the holder's cash, credit the bank). */
  reimbursePost: boolean;
  reimburseBankAccount: string | null;
  reimburseBankAccountName: string | null;
  /** The account Cash, Cheque or Other credits instead of the bank account (a way of paying left out: the bank). */
  reimburseAccounts: Partial<Record<ReimburseMethod, ReimburseAccountSetting>>;
  /** Holders confirm their cash every quarter. */
  quarterlyConfirm: boolean;
  /** Days before and after the quarter end in which the confirmation is due (1 to 45). */
  confirmWindowDays: number;
  /** The original receipts must be marked as received before the last finance approval. */
  requireOriginals: boolean;
  /** Stages after the finance review, in order (at most 5). */
  financeStages: FinanceStageSetting[];
}

export interface PettyCashSettings extends Partial<PettyCashProcessSettings> {
  vatCode: string | null;
  nonVatCode: string | null;
  cashSupplier: string | null;
  cashSupplierName: string | null;
  vatTolerance: number;
  requireImage: boolean;
  requireBuyerNamed: boolean;
  maxReceiptAgeDays: number;
  maxReceiptAmount: number | null;
  blockOverLimit: boolean;
  requisitionType: string;
  reviewRole: string;
  /**
   * The SAP warehouse whose Advanced G/L rules department (cost center) claims follow (KSA: '01' HO
   * Warehouse). Project claims follow their project's warehouse (SAP project U_WH).
   */
  deptWarehouse: string | null;
  /** Returned by the server; ignored on save. */
  deptWarehouseName?: string | null;
}

export interface PettyCashHolder {
  holderId: number;
  userId: number;
  userName: string;
  email: string | null;
  sapAccount: string;
  sapAccountName: string | null;
  /**
   * The holder's auxiliary (SAP Dimension 5) on a shared cash account, e.g. PC00004 RIYADH SHIYAS on
   * 123020100: SAP tells the holders' cash apart by it, and his receipts are paid by journal entry.
   */
  auxiliaryCode: string | null;
  auxiliaryName: string | null;
  floatAmount: number | null;
  isActive: boolean;
  /** The SAP account's balance (null when SAP could not be read, or for an auxiliary of a shared account). */
  balance?: number | null;
  /** Total of the holder's claims submitted and not yet posted. */
  pendingTotal?: number;
  /** Total of the holder's claims not yet reimbursed (Petty Cash Setup). */
  outstanding?: number;
  /** The holder's latest quarterly confirmation (Petty Cash Setup). */
  lastConfirmation?: { quarter: string; confirmedAt: string; difference: number | null } | null;
  /** A handover of this holder's petty cash waiting for its confirmations. */
  pendingHandoverId?: number | null;
}

/**
 * An expense category. Its G/L account comes from SAP's Advanced G/L rules, as for a purchase through
 * PR -> PO -> receipt: per allocation type it names an SAP item (its own rules and class) or an item
 * class (U_Class). The fixed accounts are used when the rules give none (or when no item or class is set).
 */
export interface PettyCashCategory {
  categoryId: number;
  name: string;
  /** Project claims: the SAP item whose G/L rules apply (null = none). */
  projectItem: string | null;
  projectItemName?: string | null;
  /** Project claims: the item class whose G/L rules apply (ignored when an item is given). */
  projectClass: string | null;
  /** Department (cost center) claims: the SAP item whose G/L rules apply. */
  deptItem: string | null;
  deptItemName?: string | null;
  /** Department claims: the item class whose G/L rules apply. */
  deptClass: string | null;
  /** Fixed account for project claims, used when SAP's rules give none. */
  projectAccount: string | null;
  projectAccountName?: string | null;
  /** Fixed account for department claims, used when SAP's rules give none. */
  costCenterAccount: string | null;
  costCenterAccountName?: string | null;
  /** Project claims: the auxiliary (SAP Dimension 5) its receipts carry; null = none (missing on an older server). */
  projectAuxiliary?: string | null;
  projectAuxiliaryName?: string | null;
  /** Department claims: the auxiliary its receipts carry. */
  deptAuxiliary?: string | null;
  deptAuxiliaryName?: string | null;
  /** Default dimension codes for lines of this category, e.g. {'3': 'MCSR1.2'}; Dimension 5 is each side's auxiliary. */
  dims: Dims;
  vatDeductible: boolean;
  isActive: boolean;
  sortOrder: number;
}

export interface VatCodeOption { code: string; name: string; rate: number }
export interface DimensionInfo { code: number; label: string; active: boolean }
export interface CodeName { code: string; name: string }

export interface PettyCashCapabilities {
  admin: boolean;
  /** The user is an active holder in this company. */
  isHolder: boolean;
  /** May create a claim (a holder, or finance / Admin preparing one for a holder). */
  canCreate: boolean;
  canPrepareForOthers: boolean;
  /** Holds the company's finance review role. */
  finance: boolean;
  viewAll: boolean;
  /** May retry a posting SAP refused. */
  canPost: boolean;
  /** May open Petty Cash Setup. */
  setup: boolean;
  /** May create SAP vendors (the Suppliers function). */
  suppliers: boolean;
}

export interface PettyCashConfig {
  company: {
    companyId: number;
    name: string;
    databaseName: string;
    vatRegistered: boolean;
    vatCountry: string | null;
    pettyCashEnabled: boolean;
    currency: string;
  };
  settings: PettyCashSettings;
  holder: PettyCashHolder | null;
  /** Active holders, for finance / Admin preparing a claim for one of them (empty for others). */
  holders: PettyCashHolder[];
  categories: PettyCashCategory[];
  capabilities: PettyCashCapabilities;
  dimensions: DimensionInfo[];
  vatCodes: VatCodeOption[];
  /** Facility types and cycles of the company's PR flows for the claim's requisition type. */
  routeOptions: { facilityTypes: string[]; cycles: string[] };
  /** The handovers this user must confirm (optional: older servers). */
  pendingHandovers?: HandoverView[];
  /** This user's quarterly confirmation, while one is due (optional: older servers). */
  confirmationDue?: { quarter: string; windowEnd: string; confirmed: boolean } | null;
}

/** Read from a ZATCA QR code on the receipt (seller, VAT number, date and time, total with VAT, VAT). */
export interface QrData {
  seller?: string;
  vatNumber?: string;
  timestamp?: string;
  totalWithVat?: string;
  vatTotal?: string;
}

export interface LineCheck {
  level: 'ok' | 'warning' | 'error';
  code: string;
  message: string;
}

export interface ClaimAttachment {
  attachmentId: number;
  lineId: number | null;
  fileName: string;
  mimeType: string | null;
  size: number | null;
  uploadedAt: string;
  /** The same file (by content) attached elsewhere, e.g. a receipt claimed twice. */
  duplicates?: Array<{ claimNo: string | null; claimId: number; lineNum: number | null }>;
}

/** What the screen sends for a line. */
export interface ClaimLineInput {
  lineId?: number;
  lineNum: number;
  lineType: LineType;
  /** VAT line: the SAP vendor. Non-VAT line: null (posted to the company's cash supplier). */
  cardCode: string | null;
  supplierName: string;
  supplierVat: string | null;
  invoiceNo: string;
  invoiceDate: string;
  /** Before VAT. */
  amount: number;
  vatRate: number;
  vatAmount: number;
  total: number;
  vatCode: string | null;
  categoryId: number | null;
  /** The auxiliary (SAP Dimension 5) chosen on the receipt, from the codes its account takes. */
  auxiliary?: string | null;
  description: string | null;
  /** The invoice names the company and its VAT number. */
  buyerNamed: boolean;
  qr?: QrData | null;
}

/** A receipt's account for its category and the claim's side, with the auxiliaries that account takes. */
export interface ReceiptAccount {
  account: string | null;
  accountName: string | null;
  /** 'rule' = SAP's advanced G/L rules; 'category' = the category's fixed account. */
  source: 'rule' | 'category' | null;
  rule: { code: string; description: string } | null;
  /** Why the rules give no account, when the category names an item or class. */
  note: string | null;
  auxType: string | null;
  auxMandatory: boolean;
  /** The category's default auxiliary for this side, offered first. */
  defaultAuxiliary: string | null;
  /** The codes the account takes (null when it takes no auxiliary). */
  auxiliaries: AuxiliaryList | null;
}

/** A line as the server returns it. */
export interface ClaimLine extends ClaimLineInput {
  lineId: number;
  categoryName: string | null;
  status: LineStatus;
  disallowReason: string | null;
  /** Finance's account for this line (null = SAP's rule, else the category's fixed account). */
  glAccount: string | null;
  /** The account the invoice will use (finance's, else SAP's Advanced G/L rule, else the category's fixed account). */
  effectiveAccount: string | null;
  effectiveAccountName: string | null;
  /** Where effectiveAccount comes from. */
  accountSource?: AccountSource | null;
  /** The SAP rule behind ruleAccount (also given when finance overrode it). */
  accountRule?: LineAccountRule | null;
  /** The account SAP's rules give this line. */
  ruleAccount?: string | null;
  ruleAccountName?: string | null;
  /** The category's fixed account for the claim's allocation. */
  categoryAccount?: string | null;
  categoryAccountName?: string | null;
  /** The signed-in user may change this line now (finance review, or a refused line after a posting failure). */
  canCorrect?: boolean;
  /** The account's auxiliary rule: a Dimension 5 code of this type (required when `mandatory`). */
  accountAux?: AccountAuxRule | null;
  /** The dimension codes the invoice line will carry. */
  dims: Dims;
  dimsOverride: Dims | null;
  sapInvoiceNum: string | null;
  sapPaymentNum: string | null;
  /** How SAP paid it: an outgoing cash payment, or a journal entry (a holder with an auxiliary). */
  sapPaymentKind?: 'Payment' | 'Journal' | null;
  postingError: string | null;
  attachments: ClaimAttachment[];
  checks: LineCheck[];
}

/** line: finance's account; rule: SAP's Advanced G/L rule; category: the category's fixed account. */
export type AccountSource = 'line' | 'rule' | 'category';

/** The SAP Advanced G/L rule that gives a line its account. */
export interface LineAccountRule {
  code: string;
  description: string;
  warehouseCode: string | null;
  warehouseType: string | null;
  itemCode: string | null;
  itemClass: string | null;
  /** true: the cost account (consumed at once, as the receiving route's Goods Issue); false: stays on the inventory (clearing) account. */
  issued: boolean;
  reason: string;
}

export interface ClaimHeaderInput {
  companyId: number;
  /** Who the claim is for (null = the signed-in user). */
  holderUserId?: number | null;
  allocationType: AllocationType;
  allocationCode: string;
  /** Cost center claims: the project the lines carry (required where the company's PR rule asks for one). */
  projectCode?: string | null;
  dims: Dims;
  facilityType: string;
  cycle: string;
  purpose: string | null;
}

/** What the holder types in the imprest check (numbers of 0 or more, or null). */
export interface ImprestInput {
  advances: number | null;
  cashInHand: number | null;
  note: string | null;
}

export interface ClaimSaveInput extends ClaimHeaderInput {
  lines: ClaimLineInput[];
  /** Sent while the holder may change the imprest figures (permissions.canEditImprest). */
  imprest?: ImprestInput;
}

export interface ClaimHeader {
  claimId: number;
  claimNo: string | null;
  companyId: number;
  status: ClaimStatus;
  holderUserId: number;
  holderName: string;
  createdBy: number;
  createdByName: string;
  sapAccount: string | null;
  sapAccountName: string | null;
  /** The holder's auxiliary on the cash account, kept from the submission. */
  auxiliaryCode?: string | null;
  auxiliaryName?: string | null;
  allocationType: AllocationType;
  allocationCode: string;
  allocationName: string | null;
  projectCode: string | null;
  contractType: string | null;
  dims: Dims;
  facilityType: string | null;
  cycle: string | null;
  purpose: string | null;
  currency: string | null;
  totalBeforeVat: number;
  totalVat: number;
  total: number;
  flowName: string | null;
  currentStep: number | null;
  postingDate: string | null;
  submittedAt: string | null;
  postedAt: string | null;
  lastComment: string | null;
  createdAt: string;
  updatedAt: string;
  /** The SAP warehouse whose G/L rules the claim follows (project: its warehouse; department: the setup's). */
  glWarehouseCode?: string | null;
  glWarehouseType?: string | null;
}

export interface ApprovalStageView {
  step: number;
  name: string;
  rule: 'ANY' | 'ALL';
  kind: 'PR' | 'Finance';
  status: 'Done' | 'Current' | 'Waiting';
  approvers: Array<{ userId: number; name: string; status: 'Pending' | 'Approved' | 'Skipped'; decidedAt: string | null }>;
}

export interface HistoryEntry {
  at: string;
  userName: string | null;
  action: string;
  stage: string | null;
  comment: string | null;
}

export interface ClaimPermissions {
  canEdit: boolean;
  canSubmit: boolean;
  canCancel: boolean;
  /** Approver of the current PR stage. */
  canDecide: boolean;
  /** Finance reviewer of a claim in finance review. */
  canFinanceReview: boolean;
  canRetry: boolean;
  canViewAccounting: boolean;
  /** Partly posted: this user may correct the receipts not yet in SAP, then press Post again. */
  canCorrect?: boolean;
  // Oct 2026 (optional: older servers leave them out, which means no).
  /** May change the advances, cash in hand and note of the imprest check (saved with the claim). */
  canEditImprest?: boolean;
  /** May record the reimbursement of a posted claim. */
  canReimburse?: boolean;
  /** May post again the top-up journal SAP refused (or that hangs). */
  canRetryReimbursement?: boolean;
  /** May mark the original receipts as received, or remove the mark. */
  canMarkOriginals?: boolean;
  canPrintVoucher?: boolean;
  /** At a finance stage after the first: may send the claim back to the previous finance stage. */
  canRevertFinance?: boolean;
}

/** The imprest check of a claim: float - earlier claims not yet reimbursed - this claim - advances = expected cash. */
export interface ImprestView {
  floatAmount: number | null;
  outstanding: number;
  thisClaim: number;
  advances: number | null;
  cashInHand: number | null;
  expectedCash: number | null;
  difference: number | null;
  note: string | null;
  /** The snapshot taken at submission. */
  frozen: boolean;
  checkMode: ImprestCheckMode;
  tolerance: number;
}

export interface ReimbursementView {
  amount: number;
  /** YYYY-MM-DD */
  date: string;
  method: ReimburseMethod;
  reference: string | null;
  note: string | null;
  byName: string | null;
  at: string;
  /** The account the SAP top-up credits (null when it is not posted in SAP; missing on an older server). */
  credit?: { account: string; auxiliary: string | null } | null;
  /** The SAP top-up journal (null when reimbursements are not posted in SAP). */
  sap: { status: 'Posting' | 'Posted' | 'Failed'; entry: number | null; number: string | null; error: string | null } | null;
}

/** Working days from the claim reaching finance to its reimbursement (or today). */
export interface TargetView {
  receivedAt: string | null;
  targetDays: number;
  waitingWorkingDays: number | null;
  overdue: boolean;
}

export interface OriginalsView {
  /** Required before the last finance approval. */
  required: boolean;
  receivedAt: string | null;
  receivedByName: string | null;
  /** The transmittal number they came with. */
  reference: string | null;
  note: string | null;
}

/** Where a claim in finance review is among the finance stages. */
export interface FinanceStageInfo {
  stageIndex: number | null;
  stageCount: number;
  isLastStage: boolean;
  stageName: string | null;
}

export interface ClaimDetail {
  claim: ClaimHeader;
  lines: ClaimLine[];
  /** Files attached to the claim itself (lineId null). Line files are on each line. */
  attachments: ClaimAttachment[];
  history: HistoryEntry[];
  approval: { stages: ApprovalStageView[]; currentStep: number | null };
  permissions: ClaimPermissions;
  /** Checks on the whole claim (holder balance, missing setup...). */
  checks: LineCheck[];
  // Oct 2026 (optional: an older server leaves them out).
  imprest?: ImprestView;
  reimbursement?: ReimbursementView | null;
  /** null while the claim never reached finance. */
  target?: TargetView | null;
  originals?: OriginalsView;
  displayStatus?: DisplayStatus;
  finance?: FinanceStageInfo;
}

export interface ClaimRow {
  claimId: number;
  claimNo: string | null;
  status: ClaimStatus;
  holderName: string;
  allocationType: AllocationType;
  allocationCode: string;
  allocationName: string | null;
  total: number;
  totalVat: number;
  lineCount: number;
  currentStageName: string | null;
  submittedAt: string | null;
  updatedAt: string;
  waitingForMe: boolean;
  // Oct 2026 (optional: older servers).
  /** ClaimStatus, or Reimbursed. */
  displayStatus?: string;
  reimbursedAt?: string | null;
  reimburseReference?: string | null;
  /** Working days since the claim reached finance (to its reimbursement); null before finance. */
  waitingWorkingDays?: number | null;
  overdue?: boolean;
  originalsReceived?: boolean;
  originalsRequired?: boolean;
  // Nov 2026: bulk approval and reimbursement (optional: older servers).
  holderUserId?: number;
  /** The receipts SAP paid: what a reimbursement pays back. */
  paidTotal?: number;
  /** Waiting for this user's finance decision. */
  canBulkApprove?: boolean;
  /** At the last finance stage: approving it posts it to SAP. Null outside finance review. */
  lastFinanceStage?: boolean | null;
  /** Posted, not reimbursed, of another holder, and the user may record reimbursements. */
  canReimburse?: boolean;
}

export interface AccountingEntry {
  account: string;
  accountName: string | null;
  /** The business partner on a control-account entry. */
  bp: string | null;
  debit: number;
  credit: number;
  dims?: Dims;
  projectCode?: string | null;
}

export interface AccountingDocument {
  lineId: number;
  lineNum: number;
  lineType: LineType;
  status: LineStatus;
  disallowed: boolean;
  invoice: {
    cardCode: string;
    cardName: string;
    numAtCard: string;
    docDate: string;
    taxDate: string;
    vatCode: string | null;
    entries: AccountingEntry[];
  };
  payment: {
    /** Payment = an outgoing payment; Journal = a journal entry reconciled with the invoice (auxiliary holders). */
    kind?: 'Payment' | 'Journal';
    cashAccount: string;
    cashAccountName: string | null;
    /** The holder's auxiliary on the cash line. */
    auxiliary?: string | null;
    entries: AccountingEntry[];
  };
  sapInvoiceNum: string | null;
  sapPaymentNum: string | null;
}

export interface AccountingPreview {
  postingDate: string;
  cashAccount: string;
  cashAccountName: string | null;
  documents: AccountingDocument[];
  /** Net effect by account (vendor accounts net to zero and are left out). */
  net: AccountingEntry[];
  totals: { debit: number; credit: number };
  checks: LineCheck[];
}

/** Finance's change to one line (only the keys sent are changed). */
export interface FinanceLineChange {
  glAccount?: string | null;
  vatCode?: string | null;
  dimsOverride?: Dims | null;
  lineType?: LineType;
  /** A reason disallows the line; null allows it again. */
  disallowReason?: string | null;
}

export interface VendorMatch {
  cardCode: string;
  cardName: string;
  vat: string;
  active: boolean;
  payTermsCode: number | null;
  /** True for the company's "immediately" payment terms (SAP -1). */
  cashTerms: boolean;
  /** The VAT number SAP holds is valid for the company's country. */
  vatValid: boolean;
}

export interface VendorLookup {
  /** The typed number, digits only. */
  digits: string;
  formatOk: boolean;
  formatMessage: string | null;
  vendors: VendorMatch[];
}

export interface RoutePreview {
  flowName: string | null;
  stages: Array<{ step: number; name: string; approvers: string[] }>;
  financeRole: string;
  /** Why no single PR flow matches, in the PR page's words. */
  error: string | null;
}

export interface ProjectDefaults {
  code: string;
  name: string;
  contractType: string | null;
  dims: Dims;
}

export interface PettyCashSetup {
  settings: PettyCashSettings;
  holders: PettyCashHolder[];
  categories: PettyCashCategory[];
  /** Users who can access the company (holder picker). */
  users: Array<{ userId: number; name: string; email: string | null }>;
  /** Contract types found on the company's SAP projects (U_C_TYPE). */
  contractTypes: string[];
  /** SAP item classes (OITM U_Class valid values: code = value, name = description); [] when the company has none. */
  itemClasses?: CodeName[];
  /** Active SAP warehouses with their type (U_WH_TYPE), for the department warehouse. */
  warehouses?: Array<{ code: string; name: string; type: string | null }>;
  vatCodes: VatCodeOption[];
  dimensions: DimensionInfo[];
  company: PettyCashConfig['company'];
  /** The auxiliary rule of the accounts on the page (holders' and categories'), by account code. */
  accountAux?: Record<string, AccountAuxRule>;
  /** SAP's Cost Center Types (the auxiliary types), e.g. P Cash = Petty Cash. */
  auxiliaryTypes?: Array<{ code: string; name: string }>;
}

/** An account's auxiliary rule (SAP U_AUX_TYPE / U_Aux_Mand on the G/L account). */
export interface AccountAuxRule {
  type: string | null;
  mandatory: boolean;
}

/** A cash account a holder can pay from, with its auxiliary rule. */
export interface CashAccountOption {
  code: string;
  name: string;
  balance: number | null;
  auxType?: string | null;
  auxMandatory?: boolean;
}

/** The auxiliaries (Dimension 5 codes) an account takes. */
export interface AuxiliaryList {
  account: { code: string; name: string; auxType: string | null; auxMandatory: boolean } | null;
  type: string | null;
  typeName: string | null;
  codes: Array<{ code: string; name: string; type: string | null }>;
}

// ---------------------------------------------------------------- SAP's Advanced G/L rules (setup preview)

export interface GlRuleRef { absoluteEntry: number; code: string; description: string }

/** What SAP's rules give a category for one warehouse type (project) or the department warehouse. */
export interface GlPreviewRow {
  /** "Normal", "Charge Back"... or "01 HO Warehouse". */
  label: string;
  warehouseType: string | null;
  warehouseCode: string | null;
  /** Warehouses of that type (project rows); 1 for the department row. */
  warehouseCount: number;
  account: string | null;
  accountName: string | null;
  /** true: the cost account (consumed); false: stays on the inventory (clearing) account. */
  issued: boolean;
  reason: string;
  /** null: no rule gives an account, so the fixed account applies. */
  rule: GlRuleRef | null;
  /** The category's fixed account, used when rule is null. */
  fixedAccount: string | null;
  /** The account's auxiliary type (Dimension 5), e.g. 'UTILITIE'. */
  auxType: string | null;
  auxMandatory: boolean;
}

export interface GlPreviewSide {
  /** The item given (name '' when SAP lacks it). */
  item: { code: string; name: string; itemClass: string | null } | null;
  /** An item code SAP does not have. */
  itemMissing: boolean;
  /** The class the rules use: the item's own, or the chosen class. */
  itemClass: string | null;
  /** Project: one row per warehouse type; department: one row (or none). */
  rows: GlPreviewRow[];
}

export interface GlPreview {
  /** The financial year of the rule set (today's). */
  year: number | null;
  /** null when neither an item nor a class was given for projects. */
  project: GlPreviewSide | null;
  /** null when neither an item nor a class was given for departments. */
  department: GlPreviewSide | null;
  /** The department warehouse used (null / '' = not set: a warning says so). */
  deptWarehouse: string | null;
  warnings: string[];
}

export interface GlPreviewParams {
  projectItem?: string | null;
  projectClass?: string | null;
  deptItem?: string | null;
  deptClass?: string | null;
  projectAccount?: string | null;
  costCenterAccount?: string | null;
}

// ---------------------------------------------------------------- handovers and quarterly confirmations (Oct 2026)

/** A holder hands his petty cash to a successor: the outgoing holder counts and confirms, then the successor. */
export interface HandoverView {
  handoverId: number;
  companyId: number;
  status: 'Pending' | 'Completed' | 'Cancelled';
  from: { userId: number; name: string };
  to: { userId: number; name: string };
  sapAccount: string;
  sapAccountName: string | null;
  auxiliaryCode: string | null;
  auxiliaryName: string | null;
  floatAmount: number | null;
  cashCounted: number | null;
  advances: number | null;
  outstanding: number | null;
  outstandingClaims: Array<{ claimId: number; claimNo: string | null; total: number; status: string }>;
  note: string | null;
  fromNote: string | null;
  toNote: string | null;
  createdByName: string | null;
  createdAt: string;
  fromConfirmedAt: string | null;
  toConfirmedAt: string | null;
  completedAt: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;
  canConfirmFrom: boolean;
  canConfirmTo: boolean;
  canCancel: boolean;
}

export interface HandoverList {
  /** Setup or finance: sees every handover of the company (others only their own). */
  canManage: boolean;
  handovers: HandoverView[];
}

export interface HandoverInput {
  companyId: number;
  fromUserId: number;
  toUserId: number;
  sapAccount: string;
  auxiliaryCode: string | null;
  floatAmount: number | null;
  note: string | null;
}

export interface HandoverConfirmInput {
  side: 'from' | 'to';
  /** 'from' only: the cash counted (required) and the advances. */
  cashCounted?: number;
  advances?: number | null;
  note?: string | null;
}

export interface ConfirmationView {
  confirmationId: number;
  /** '2026-Q4' */
  quarter: string;
  floatAmount: number | null;
  outstanding: number;
  advances: number | null;
  cashInHand: number;
  expectedCash: number | null;
  difference: number | null;
  note: string | null;
  confirmedAt: string;
  reviewedAt: string | null;
  reviewedByName: string | null;
  reviewNote: string | null;
}

export type ConfirmationState = 'Confirmed' | 'Difference' | 'Missing' | 'Reviewed';

export interface ConfirmationRow {
  holderId: number;
  userId: number;
  holderName: string;
  floatAmount: number | null;
  state: ConfirmationState;
  confirmation: ConfirmationView | null;
}

/** A quarter's confirmations of every holder with a float (finance or setup). */
export interface ConfirmationList {
  quarter: string;
  windowStart: string;
  windowEnd: string;
  canReview: boolean;
  rows: ConfirmationRow[];
}

/** The signed-in holder's confirmation: the quarter due (null when none is), and his figures. */
export interface MyConfirmation {
  due: { quarter: string; windowStart: string; windowEnd: string } | null;
  confirmation: ConfirmationView | null;
  figures: { floatAmount: number | null; outstanding: number } | null;
}

export interface ConfirmationInput {
  companyId: number;
  quarter: string;
  advances: number | null;
  cashInHand: number;
  note: string | null;
  acknowledged: true;
}
